import React from 'react';
import styled from 'styled-components';

const FooterContainer = styled.footer`
  background-color: var(--dark-color);
  color: var(--light-color);
  padding: 4rem 2rem 2rem;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLogo = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--accent-color);
`;

const FooterDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  opacity: 0.8;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--accent-color);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--accent-color);
    color: var(--dark-color);
    transform: translateY(-3px);
  }
`;

const FooterTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  color: white;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 30px;
    height: 2px;
    background-color: var(--accent-color);
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 0.8rem;
  
  a {
    color: rgba(255, 255, 255, 0.7);
    transition: all 0.3s ease;
    font-size: 0.9rem;
    display: inline-block;
    
    &:hover {
      color: var(--accent-color);
      transform: translateX(5px);
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  
  svg {
    color: var(--accent-color);
    min-width: 20px;
  }
`;

const CopyrightBar = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 2rem;
  margin-top: 3rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
`;

const FooterNav = styled.div`
  display: flex;
  gap: 1.5rem;
  
  a {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--accent-color);
    }
  }
`;

const Footer: React.FC = () => {
  return (
    <FooterContainer id="contact">
      <FooterContent>
        <FooterColumn>
          <FooterLogo>Flinstones RestoCafe</FooterLogo>
          <FooterDescription>
            Eşsiz lezzetler ve sıcak atmosfer ile unutulmaz anlar yaşayın. Doğal malzemeler ve özgün tariflerle hazırlanan özel menümüzü keşfedin.
          </FooterDescription>
          <SocialLinks>
            <SocialIcon href="#" aria-label="Facebook">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
            </SocialIcon>
            <SocialIcon href="#" aria-label="Instagram">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </SocialIcon>
            <SocialIcon href="#" aria-label="Twitter">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
              </svg>
            </SocialIcon>
          </SocialLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>Özel Menüler</FooterTitle>
          <FooterDescription style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
            Günlük taze hazırlanan özel menülerimizi ve sezonluk lezzetleri keşfedin.
          </FooterDescription>
          <FooterLinks>
            <FooterLink>
              <a href="#menu" rel="noopener noreferrer">
                🍽️ Günün Menüsü
              </a>
            </FooterLink>
            <FooterLink>
              <a href="#menu" rel="noopener noreferrer">
                ☕ Kahve Çeşitleri
              </a>
            </FooterLink>
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>Hizmetlerimiz</FooterTitle>
          <FooterLinks>
            <FooterLink><a href="/rezervasyon">Rezervasyon</a></FooterLink>
            <FooterLink><a href="/etkinlikler">Özel Etkinlikler</a></FooterLink>
            <FooterLink><a href="/catering">Catering Hizmeti</a></FooterLink>
            <FooterLink><a href="/dogum-gunu">Doğum Günü Organizasyonu</a></FooterLink>
            <FooterLink><a href="/paket-servis">Paket Servis</a></FooterLink>
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>İletişim</FooterTitle>
          <ContactInfo>
            <ContactItem>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>mahallesi, 2000 Evler, 35. Yol no:19/1, 50100 Nevşehir Merkez/Nevşehir</span>
            </ContactItem>
            <ContactItem>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <span>(0384) 215 02 15</span>
            </ContactItem>
            <ContactItem>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <span>info@flinstonesrestocafe.com</span>
            </ContactItem>
          </ContactInfo>
        </FooterColumn>
      </FooterContent>
      
      <CopyrightBar>
        <Copyright>&copy; {new Date().getFullYear()} Flinstones RestoCafe. Tüm hakları saklıdır.</Copyright>
        <FooterNav>
          <a href="/privacy">Gizlilik Politikası</a>
          <a href="/terms">Kullanım Şartları</a>
          <a href="/cookies">Çerezler</a>
        </FooterNav>
      </CopyrightBar>
    </FooterContainer>
  );
};

export default Footer; 